import { Calculator, Users, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

const APP_CONFIG = {
  compta: {
    label: 'Nexus Compta',
    icon: Calculator,
    color: '#2563eb',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
  },
  sirh: {
    label: 'Nexus SIRH',
    icon: Users,
    color: '#7c3aed',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
  },
};

const formatValue = (key, value) => {
  if (value == null || value === undefined) return '--';
  if (typeof value === 'number') {
    if (key.toLowerCase().includes('revenue') || key.toLowerCase().includes('montant') || key.toLowerCase().includes('chiffre')) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('fr-FR').format(value);
  }
  return String(value);
};

const prettifyKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;

  const chartData = data.map((v, i) => ({ i, v: typeof v === 'object' ? v.value || v.amount || 0 : v }));

  return (
    <div className="h-10 mt-3">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.2} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#spark-${color.replace('#', '')})`}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const AppCard = ({ appCode, appData }) => {
  const config = APP_CONFIG[appCode] || {
    label: appCode,
    icon: AlertTriangle,
    color: '#64748b',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-600',
  };
  const Icon = config.icon;

  if (!appData || appData.error) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bgColor}`}>
            <Icon className={`w-5 h-5 ${config.textColor}`} />
          </div>
          <h3 className="font-semibold text-slate-900">{config.label}</h3>
        </div>
        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">Service indisponible</p>
        </div>
      </div>
    );
  }

  // Separate trend data from metrics
  const { trend, trends, sparkline, ...metrics } = appData;
  const trendData = trend || trends || sparkline;

  // Filter out internal/meta keys
  const metricEntries = Object.entries(metrics).filter(
    ([key]) => !['app_code', 'app_name', 'status', 'error'].includes(key)
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bgColor}`}>
          <Icon className={`w-5 h-5 ${config.textColor}`} />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">{config.label}</h3>
          {appData.status && (
            <span className="text-xs text-green-600 font-medium">En ligne</span>
          )}
        </div>
      </div>

      {metricEntries.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {metricEntries.slice(0, 6).map(([key, value]) => (
            <div key={key} className="bg-slate-50 rounded-lg p-2.5">
              <p className="text-xs text-slate-500 truncate">{prettifyKey(key)}</p>
              <p className="text-sm font-bold text-slate-900 mt-0.5">
                {formatValue(key, value)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Aucune metrique disponible</p>
      )}

      {trendData && Array.isArray(trendData) && (
        <Sparkline data={trendData} color={config.color} />
      )}
    </div>
  );
};

const CrossAppKPIs = ({ data }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.keys(APP_CONFIG).map((appCode) => (
          <AppCard key={appCode} appCode={appCode} appData={null} />
        ))}
      </div>
    );
  }

  const appCodes = Object.keys(data).length > 0 ? Object.keys(data) : Object.keys(APP_CONFIG);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {appCodes.map((appCode) => (
        <AppCard key={appCode} appCode={appCode} appData={data[appCode]} />
      ))}
    </div>
  );
};

export default CrossAppKPIs;
